import format from 'date-fns/format';
import { useState } from 'react';

import { Calendar } from './Calendar';
import { Icon } from './Icon';
import { Input } from './Input';
import { Stack } from './Stack';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from './utils/cn';

interface DateTimePickerProps {
  dateTime?: Date;
  onDateTimeChange: (dateTime?: Date) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  dateTime,
  onDateTimeChange,
  className,
  placeholder = 'Select date and time',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const handleDateChange = (date?: Date) => {
    if (!date) {
      onDateTimeChange(undefined);
      return;
    }

    const newDateTime = new Date(date);
    if (dateTime) {
      newDateTime.setHours(dateTime.getHours());
      newDateTime.setMinutes(dateTime.getMinutes());
    }
    onDateTimeChange(newDateTime);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parts = e.target.value.split(':');
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);

    if (isNaN(hours) || isNaN(minutes)) return;

    const newDateTime = dateTime ? new Date(dateTime) : new Date();
    newDateTime.setHours(hours);
    newDateTime.setMinutes(minutes);
    newDateTime.setSeconds(0);
    newDateTime.setMilliseconds(0);
    onDateTimeChange(newDateTime);
  };

  const timeValue = dateTime
    ? `${String(dateTime.getHours()).padStart(2, '0')}:${String(dateTime.getMinutes()).padStart(2, '0')}`
    : '';

  const handleClear = () => {
    onDateTimeChange(undefined);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'rounded-sm border border-bg-quaternary bg-transparent p-2 text-text-secondary outline-hidden hover:border-border-secondary focus:border-border-accent-quaternary',
            'flex min-w-0 cursor-pointer items-center justify-between gap-2',
            'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <Icon name="Calendar" />
          {dateTime ? (
            format(dateTime, 'PPP p')
          ) : (
            <span className="text-text-tertiary">{placeholder}</span>
          )}
          <Icon name="ChevronDown" className="ml-auto" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateTime}
          defaultMonth={dateTime}
          onSelect={handleDateChange}
          autoFocus
        />
        <Stack
          className="border-t border-border-primary p-3"
          direction="horizontal"
          align="center"
          justify="between"
        >
          <Stack
            direction="horizontal"
            align="center"
            gap={2}
            fullWidth={false}
          >
            <span className="text-sm text-text-tertiary">Time:</span>
            <Input
              type="time"
              value={timeValue}
              onChange={handleTimeChange}
              className="w-auto"
            />
          </Stack>
          {dateTime && (
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-1 rounded-sm px-2 py-1 text-sm text-text-tertiary hover:bg-bg-quaternary hover:text-text-secondary"
            >
              <Icon name="X" size="sm" />
              Clear
            </button>
          )}
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
